import React from "react";
import type { ChatMessage } from "../../types";
import { DozeLogo } from "./DozeLogo";

interface Props {
  message: ChatMessage;
}

/**
 * Renders a single chat message.
 * Assistant: left-aligned, white card with the Doze mascot avatar.
 * User: right-aligned, warm charcoal bubble.
 */
export function MessageBubble({ message }: Props) {
  const isAssistant = message.role === "assistant";

  return (
    <div className={`flex w-full mb-4 ${isAssistant ? "justify-start" : "justify-end"}`}>
      {isAssistant && (
        <div className="mr-2.5 mt-0.5 flex-shrink-0">
          <DozeLogo size={30} floating={false} />
        </div>
      )}

      <div
        className={[
          "max-w-[86%] md:max-w-[80%] px-4 py-3 text-[15px] leading-relaxed",
          isAssistant
            ? "bg-white text-[#2B2A28] rounded-2xl rounded-tl-md border border-[#ECE5D6]"
            : "bg-[#2B2A28] text-[#F4F1E6] rounded-2xl rounded-tr-md",
        ].join(" ")}
      >
        {isAssistant ? (
          <AssistantContent text={message.content} />
        ) : (
          <span className="whitespace-pre-wrap break-words">{message.content}</span>
        )}
      </div>
    </div>
  );
}

/** Streaming cursor shown while text is being appended. */
export function StreamingCursor() {
  return (
    <span
      aria-hidden="true"
      className="inline-block w-2 h-4 bg-[#D2785A] ml-0.5 align-middle animate-pulse rounded-sm"
    />
  );
}

/**
 * Lightweight markdown-to-React renderer for assistant output.
 * Handles: **bold**, `code`, [text](url) links, and bare https:// URLs.
 */
function AssistantContent({ text }: { text: string }) {
  if (!text) return null;

  const lines = text.split("\n");

  return (
    <div className="space-y-1.5 break-words">
      {lines.map((line, i) => {
        if (line.trim() === "") return <div key={i} className="h-1" />;

        if (/^[-*]\s/.test(line)) {
          return (
            <div key={i} className="flex gap-2 items-start">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D2785A] flex-shrink-0" />
              <span>{renderInline(line.replace(/^[-*]\s/, ""))}</span>
            </div>
          );
        }

        return <p key={i}>{renderInline(line)}</p>;
      })}
    </div>
  );
}

/**
 * Parses inline markdown tokens from a string and returns React nodes.
 */
function renderInline(text: string): React.ReactNode[] {
  const pattern =
    /(\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s]+))/g;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[2]) {
      nodes.push(<strong key={match.index}>{match[2]}</strong>);
    } else if (match[3]) {
      nodes.push(
        <code
          key={match.index}
          className="bg-black/10 rounded px-1 py-0.5 text-xs font-mono"
        >
          {match[3]}
        </code>,
      );
    } else if (match[4] && match[5]) {
      nodes.push(
        <a
          key={match.index}
          href={match[5]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 font-medium text-[#C0603F] hover:text-[#9e4d33]"
        >
          {match[4]}
        </a>,
      );
    } else if (match[6]) {
      nodes.push(
        <a
          key={match.index}
          href={match[6]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 font-medium text-[#C0603F] hover:text-[#9e4d33] break-all"
        >
          {match[6]}
        </a>,
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : [text];
}
